import React, { useState, useEffect, useMemo } from 'react';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { addActivity } from '../utils/cardUtils';

import {
    ModalOverlay,
    ModalContent,
    ModalHeader,
    SearchInput,
    CardListContainer,
    CardListItem,
    NoResultsMessage,
    ModalActions,
    CancelButton,
} from '../styles/AddRelatedCardModal.styles';

const AddRelatedCardModal = ({
    isOpen,
    onClose,
    projectId,
    currentCardId,
    currentCardTitle, // Title of the card to which we are adding relations
    currentCardManuallyRelatedIds = [], // Existing related IDs for the current card
    allColumns,
    onRelatedCardAdded, // Callback: (updatedCurrentCard, updatedRelatedCard) => void
}) => {
    const { user } = useAuth();
    const [searchTerm, setSearchTerm] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setSearchTerm('');
        }
    }, [isOpen]);

    const findCardById = (cardIdToFind) => {
        if (!allColumns || !cardIdToFind) return null;
        for (const columnId in allColumns) {
            const column = allColumns[columnId];
            if (column.cards) {
                const foundCard = column.cards.find(c => c.id === cardIdToFind);
                if (foundCard) return { ...foundCard, columnId }; // Return card with its columnId
            }
        }
        return null;
    };

    const availableForManualRelation = useMemo(() => {
        if (!allColumns || !currentCardId) return [];
        const allFlattenedCards = [];
        for (const columnId in allColumns) {
            if (allColumns[columnId] && allColumns[columnId].cards) {
                allFlattenedCards.push(...allColumns[columnId].cards);
            }
        }
        const relatedIdsSet = new Set(currentCardManuallyRelatedIds);
        return allFlattenedCards.filter(card => {
            if (!card || !card.id) return false;
            // Cannot relate to itself, and cannot relate if already related
            return card.id !== currentCardId && !relatedIdsSet.has(card.id);
        });
    }, [allColumns, currentCardId, currentCardManuallyRelatedIds]);

    const filteredCandidates = useMemo(() => {
        if (!searchTerm) {
            return availableForManualRelation;
        }
        return availableForManualRelation.filter(card =>
            card.title.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [availableForManualRelation, searchTerm]);

    const handleSelectRelatedCard = async (selectedRelatedCardInfo) => {
        if (!selectedRelatedCardInfo || isSaving) return;

        setIsSaving(true);
        const selectedRelatedCardId = selectedRelatedCardInfo.id;

        try {
            const projectRef = doc(db, 'projects', projectId);
            const projectSnap = await getDoc(projectRef);
            if (!projectSnap.exists()) throw new Error("Project not found");
            const projectData = projectSnap.data();

            // --- Find and prepare current card for update ---
            let currentCardToUpdate = null;
            let currentCardColId = null;
            for (const colId in projectData.columns) {
                const found = projectData.columns[colId].cards.find(c => c.id === currentCardId);
                if (found) {
                    currentCardToUpdate = found;
                    currentCardColId = colId;
                    break;
                }
            }
            if (!currentCardToUpdate) throw new Error("Current card not found for update.");

            const newManuallyRelatedForCurrent = [...(currentCardToUpdate.manuallyRelatedCardIds || []), selectedRelatedCardId];
            const activityDetailsCurrent = `linked to card "${selectedRelatedCardInfo.title}" as related`;
            const newActivityForCurrent = addActivity(user, { type: 'manual_relation_added', details: activityDetailsCurrent });
            const updatedCurrentCard = {
                ...currentCardToUpdate,
                manuallyRelatedCardIds: newManuallyRelatedForCurrent,
                activity: [...(currentCardToUpdate.activity || []), newActivityForCurrent]
            };

            // --- Find and prepare selected related card for update ---
            let relatedCardToUpdate = null;
            let relatedCardColId = null;
             for (const colId in projectData.columns) {
                const found = projectData.columns[colId].cards.find(c => c.id === selectedRelatedCardId);
                if (found) {
                    relatedCardToUpdate = found;
                    relatedCardColId = colId;
                    break;
                }
            }
            if (!relatedCardToUpdate) throw new Error("Selected related card not found for update.");

            const newManuallyRelatedForSelected = [...(relatedCardToUpdate.manuallyRelatedCardIds || []), currentCardId];
            const activityDetailsSelected = `linked from card "${currentCardTitle}" as related`;
            const newActivityForSelected = addActivity(user, { type: 'manual_relation_added', details: activityDetailsSelected });
            const updatedRelatedCard = {
                ...relatedCardToUpdate,
                manuallyRelatedCardIds: newManuallyRelatedForSelected,
                activity: [...(relatedCardToUpdate.activity || []), newActivityForSelected]
            };

            // --- Prepare Firestore updates ---
            const firestoreUpdates = { updatedAt: new Date().toISOString() };
            if (currentCardColId === relatedCardColId) { // Both cards in the same column
                const updatedCardsInSharedColumn = projectData.columns[currentCardColId].cards.map(c => {
                    if (c.id === currentCardId) return updatedCurrentCard;
                    if (c.id === selectedRelatedCardId) return updatedRelatedCard;
                    return c;
                });
                firestoreUpdates[`columns.${currentCardColId}.cards`] = updatedCardsInSharedColumn;
            } else { // Cards in different columns
                const updatedCardsInCurrentCol = projectData.columns[currentCardColId].cards.map(c =>
                    c.id === currentCardId ? updatedCurrentCard : c
                );
                firestoreUpdates[`columns.${currentCardColId}.cards`] = updatedCardsInCurrentCol;

                const updatedCardsInRelatedCol = projectData.columns[relatedCardColId].cards.map(c =>
                    c.id === selectedRelatedCardId ? updatedRelatedCard : c
                );
                firestoreUpdates[`columns.${relatedCardColId}.cards`] = updatedCardsInRelatedCol;
            }

            await updateDoc(projectRef, firestoreUpdates);

            if (onRelatedCardAdded) {
                // Pass back the updated versions of both cards
                onRelatedCardAdded(updatedCurrentCard, updatedRelatedCard);
            }
            onClose();
        } catch (error) {
            console.error('Error adding related card:', error);
            alert('Failed to add related card. ' + error.message);
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <ModalOverlay onClick={onClose}>
            <ModalContent onClick={e => e.stopPropagation()}>
                <ModalHeader>Add Related Card</ModalHeader>
                <SearchInput
                    type="text"
                    placeholder="Search cards by title..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    autoFocus
                />
                <CardListContainer>
                    {filteredCandidates.length > 0 ? (
                        filteredCandidates.map(card => (
                            <CardListItem
                                key={card.id}
                                onClick={() => handleSelectRelatedCard(card)}
                                title={card.title}
                                disabled={isSaving}
                            >
                                {card.title}
                            </CardListItem>
                        ))
                    ) : (
                        <NoResultsMessage>
                            {searchTerm ? "No matching cards found." : "No available cards to relate."}
                        </NoResultsMessage>
                    )}
                </CardListContainer>
                <ModalActions>
                    <CancelButton onClick={onClose} disabled={isSaving}>Cancel</CancelButton>
                </ModalActions>
            </ModalContent>
        </ModalOverlay>
    );
};

export default AddRelatedCardModal;
